/**
 * Paie & terrain card — surfaces the code RD (matricule), the field
 * `plafond_heures_jour` cap, and the `actif_terrain_override` control
 * on the candidate profile.
 *
 * Only rendered for candidates whose status makes them plausible field
 * collaborateurs (hired). The card ALSO surfaces a manual "Attribuer
 * un code RD" button for the rare case where the automatic signature
 * hook failed — the admin can rattrap the code from here.
 */
import { useState } from "react";

import {
    AlertTriangle,
    CheckCircle2,
    Fingerprint,
    Loader2,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { hasRole } from "../../lib/roles";


function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch { return "—"; }
}


export default function PaieTerrainCard({ candidate, user, onChange }) {
    const isAdmin = hasRole(user, "admin");
    const [assigning, setAssigning] = useState(false);
    const [editingCap, setEditingCap] = useState(false);
    const [cap, setCap] = useState(candidate.plafond_heures_jour ?? "");
    const [savingCap, setSavingCap] = useState(false);
    const overrideValue = candidate.actif_terrain_override || "auto";

    // iter69 — statut d'emploi + éligibilité transport
    const [savingStatutEmploi, setSavingStatutEmploi] = useState(false);
    const [savingTransport, setSavingTransport] = useState(false);
    const STATUT_EMPLOI_OPTIONS = [
        { value: "cdd", label: "CDD" },
        { value: "cdi", label: "CDI" },
        { value: "alternant", label: "Alternant" },
        { value: "assistant", label: "Assistant" },
    ];

    async function saveStatutEmploi(next) {
        setSavingStatutEmploi(true);
        try {
            await api.patch(`/candidates/${candidate.id}`, { statut_emploi: next });
            toast.success(`Statut d'emploi → ${next.toUpperCase()}`);
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Modification échouée");
        } finally {
            setSavingStatutEmploi(false);
        }
    }

    async function saveTransport(next) {
        setSavingTransport(true);
        try {
            await api.patch(`/candidates/${candidate.id}`, { eligible_transport: next });
            toast.success(next ? "Transport activé" : "Transport désactivé");
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Modification échouée");
        } finally {
            setSavingTransport(false);
        }
    }

    async function handleAssign() {
        if (!isAdmin) return;
        if (candidate.is_test) {
            toast.error("Les candidats de test ne peuvent pas recevoir de code RD");
            return;
        }
        setAssigning(true);
        try {
            const { data } = await api.post(
                `/admin/code-rd/candidates/${candidate.id}/assign`,
                { note: "Attribution manuelle depuis la fiche candidat" },
            );
            toast.success(
                data.source === "historical"
                    ? `Code historique ${data.code} rattaché`
                    : `Code ${data.code} attribué`
            );
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Attribution échouée");
        } finally {
            setAssigning(false);
        }
    }

    async function saveCap() {
        if (!isAdmin) return;
        setSavingCap(true);
        try {
            const parsed = cap === "" ? null : parseFloat(cap);
            if (parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > 24)) {
                toast.error("Plafond invalide (0 à 24 heures/jour ou vide)");
                setSavingCap(false); return;
            }
            await api.patch(`/candidates/${candidate.id}`, {
                plafond_heures_jour: parsed,
            });
            toast.success(parsed === null ? "Plafond retiré" : `Plafond fixé à ${parsed} h/j`);
            setEditingCap(false);
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Sauvegarde échouée");
        } finally {
            setSavingCap(false);
        }
    }

    async function setOverride(next) {
        if (!isAdmin) return;
        try {
            await api.patch(`/candidates/${candidate.id}`, {
                actif_terrain_override: next === "auto" ? null : next,
            });
            toast.success(
                next === "auto" ? "Statut terrain remis en automatique"
                    : `Statut terrain forcé ${next === "actif" ? "actif" : "inactif"}`
            );
            onChange?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Modification échouée");
        }
    }

    return (
        <div
            data-testid="paie-terrain-card"
            className="card-soft p-6 md:p-7"
        >
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-shariti-orange" />
                    <h3 className="text-lg font-black text-shariti-black">Paie & terrain</h3>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Données réservées à l'écosystème PayFit · Booking · Reporting
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Statut d'emploi (iter69) */}
                <div className="rounded-xl border border-shariti-border bg-slate-50/60 p-4 md:col-span-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Statut d'emploi
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                        Type de contrat porté par la personne. Défaut CDD.
                    </div>
                    {isAdmin ? (
                        <select
                            value={candidate.statut_emploi || "cdd"}
                            onChange={(e) => saveStatutEmploi(e.target.value)}
                            disabled={savingStatutEmploi}
                            data-testid="paie-terrain-statut-emploi-select"
                            className="w-full px-3 py-1.5 rounded-lg border border-shariti-border bg-white text-sm font-bold uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange"
                        >
                            {STATUT_EMPLOI_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    ) : (
                        <div className="text-sm font-black text-shariti-black uppercase" data-testid="paie-terrain-statut-emploi-readonly">
                            {(candidate.statut_emploi || "cdd").toUpperCase()}
                        </div>
                    )}
                </div>

                {/* Éligibilité transport (iter69) */}
                <div className="rounded-xl border border-shariti-border bg-slate-50/60 p-4 md:col-span-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Éligible transport
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                        Booléen simple. Le montant est calculé côté Reporting.
                    </div>
                    {isAdmin ? (
                        <div className="flex gap-1.5" data-testid="paie-terrain-transport-toggle">
                            {[
                                { key: true,  label: "Éligible" },
                                { key: false, label: "Non éligible" },
                            ].map(o => (
                                <button
                                    key={String(o.key)}
                                    type="button"
                                    onClick={() => saveTransport(o.key)}
                                    disabled={savingTransport}
                                    data-testid={`paie-terrain-transport-${o.key ? "on" : "off"}`}
                                    className={`flex-1 px-2 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition ${(candidate.eligible_transport ?? true) === o.key ? "bg-shariti-orange text-white" : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm font-semibold text-slate-700">
                            {(candidate.eligible_transport ?? true) ? "Éligible" : "Non éligible"}
                        </div>
                    )}
                </div>

                {/* Matricule (code_rd) */}
                <div className="rounded-xl border border-shariti-border bg-slate-50/60 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Matricule Shariti
                    </div>
                    {candidate.code_rd ? (
                        <>
                            <div
                                data-testid="paie-terrain-code-rd"
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-base font-black tracking-wider bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/60"
                            >
                                <Fingerprint className="w-4 h-4" />
                                {candidate.code_rd}
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500">
                                Attribué le <strong>{fmtDate(candidate.code_rd_attributed_at)}</strong>
                                {candidate.code_rd_history?.[0]?.kind && (
                                    <> · <span className="italic">{candidate.code_rd_history.at(-1)?.kind === "historical_link" ? "récupération historique" : candidate.code_rd_history.at(-1)?.kind}</span></>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="space-y-2">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-sm font-black bg-slate-100 text-slate-400 border border-dashed border-slate-300">
                                <Fingerprint className="w-4 h-4" />
                                Sans code
                            </div>
                            {candidate.is_test ? (
                                <p className="text-[11px] text-slate-400 italic">
                                    Candidat de test — jamais éligible à un code RD.
                                </p>
                            ) : candidate.status === "hired" && isAdmin ? (
                                <button
                                    type="button"
                                    onClick={handleAssign}
                                    disabled={assigning}
                                    data-testid="paie-terrain-assign-code-rd"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-60 text-white text-[11px] font-black uppercase tracking-wider transition"
                                >
                                    {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                    Attribuer un code
                                </button>
                            ) : candidate.status !== "hired" ? (
                                <p className="text-[11px] text-slate-500 italic flex items-start gap-1">
                                    <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                                    Le code sera attribué automatiquement à la signature du contrat.
                                </p>
                            ) : null}
                        </div>
                    )}
                </div>

                {/* Plafond heures / jour */}
                <div className="rounded-xl border border-shariti-border bg-slate-50/60 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            Plafond heures / jour
                        </div>
                        {isAdmin && !editingCap && (
                            <button
                                type="button"
                                onClick={() => { setCap(candidate.plafond_heures_jour ?? ""); setEditingCap(true); }}
                                className="text-[10px] font-black uppercase tracking-wider text-shariti-orange hover:underline"
                                data-testid="paie-terrain-edit-plafond"
                            >
                                Modifier
                            </button>
                        )}
                    </div>
                    {editingCap ? (
                        <div className="space-y-2">
                            <input
                                type="number" step="0.25" min="0" max="24"
                                value={cap}
                                onChange={(e) => setCap(e.target.value)}
                                placeholder="Vide = aucun plafond"
                                data-testid="paie-terrain-plafond-input"
                                className="w-full px-3 py-1.5 rounded-lg border border-shariti-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={saveCap}
                                    disabled={savingCap}
                                    data-testid="paie-terrain-plafond-save"
                                    className="flex-1 px-3 py-1.5 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-60 text-white text-[11px] font-black uppercase tracking-wider"
                                >
                                    {savingCap ? "…" : "Enregistrer"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setEditingCap(false); setCap(candidate.plafond_heures_jour ?? ""); }}
                                    className="px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100 text-[11px] font-black uppercase tracking-wider"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="text-2xl font-black text-shariti-black">
                                {candidate.plafond_heures_jour != null ? `${candidate.plafond_heures_jour} h` : <span className="text-slate-400 text-base font-semibold">Aucun plafond</span>}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                {candidate.plafond_heures_jour != null
                                    ? "Utilisé par Booking pour capper les heures validées"
                                    : "Booking accepte toutes les heures pointées"}
                            </div>
                        </div>
                    )}
                </div>

                {/* Actif terrain */}
                <div className="rounded-xl border border-shariti-border bg-slate-50/60 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Actif terrain
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                        Exposé à Booking + Reporting via l'API service. Par défaut : automatique
                        (calculé depuis le statut hired + contrat couvrant aujourd'hui).
                    </div>
                    {isAdmin ? (
                        <div className="flex gap-1.5" data-testid="paie-terrain-actif-override">
                            {[
                                { key: "auto", label: "Auto" },
                                { key: "actif", label: "Forcé actif" },
                                { key: "inactif", label: "Forcé inactif" },
                            ].map(o => (
                                <button
                                    key={o.key}
                                    type="button"
                                    onClick={() => setOverride(o.key)}
                                    data-testid={`paie-terrain-actif-${o.key}`}
                                    className={`flex-1 px-2 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition ${overrideValue === o.key ? "bg-shariti-orange text-white" : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm font-semibold text-slate-700">
                            Mode : <span className="text-shariti-orange">{overrideValue === "auto" ? "automatique" : overrideValue}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Attribution history - collapsible list */}
            {candidate.code_rd_history?.length > 0 && (
                <details className="mt-4 group" data-testid="paie-terrain-history">
                    <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-shariti-orange transition select-none">
                        Historique des attributions ({candidate.code_rd_history.length})
                    </summary>
                    <div className="mt-2 space-y-1.5 text-xs text-slate-600">
                        {candidate.code_rd_history.map((h, i) => (
                            <div key={i} className="flex items-center gap-2 rounded-md bg-white border border-slate-100 px-2.5 py-1.5">
                                <span className="font-mono text-shariti-orange font-black">{h.code}</span>
                                <span className="text-slate-400">·</span>
                                <span>{h.kind === "historical_link" ? "rattachement historique" : h.kind === "auto" ? "auto (signature)" : h.kind}</span>
                                <span className="text-slate-400">·</span>
                                <span>{fmtDate(h.at)}</span>
                                {h.by_name && <><span className="text-slate-400">·</span><span>{h.by_name}</span></>}
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}
